import _ from 'lodash';
import { createSelector } from 'reselect';

import {
  getCachedSupportData,
  getComponentProps,
  getDataSources,
  getDeltaChanges,
  getEntityData,
  getEntityDefinition,
  getEntityType,
  getErrors,
  getFormData,
  getFormInfo,
  getFormModel,
  getLayoutChanges,
  getNetworkStatus,
  getOriginalFormModel,
  getRelationShipKeysMap,
  getSupportData,
  getTranslations,
  getUnsynchronizedEntityId,
  getUserRoles,
  getWorkflowInfo,
} from './common.selectors';
import { getIsOfflineSupportAvailable } from './offlineMode.selectors';
import { ENTITY_DISPLAY } from '../../constants/Urls';
import { getAppSettings, getOrgHierarchy } from '../../selectors/AppConfigsSelectors';
import { getInstalledVersionsRows } from '../../selectors/InstalledVersionsSelectors';
import { getManageRights } from '../../selectors/MasterSetupPageSelectors';
import { includesValueInOptions, isTrue } from '../../utils/commonUtils';
import { formatStringToDateType } from '../../utils/dateUtils';
import { isNilOrEmpty } from '../../utils/ramdaUtils';
import {
  BOUND_CONTAINERS,
  BUTTON_LIST,
  BUTTON_TYPES,
  FORM_KEYS,
  FORM_TYPES,
  PREDEFINED_DATA_SOURCES,
  REPEATING_CONTAINERS,
  ROOT,
  SS_CONTROL_TYPE,
} from '../constants/form-manager.constants';
import { OFFLINE_ENTITY_TYPE } from '../constants/runtime.constants';
import { evalSSEexpression, evaluateConditions } from '../utils/evaluation.utils';
import {
  applyFilterToDataSource,
  findClosestContainerParent,
  findClosestParentWithEntityName,
  getDataPath,
  getDataType,
  getEntityNameForZoom,
  getEntityPrimaryKey,
  getGridArrIndexBasedOnRequiredLength,
  getParentEntityName,
  getParentNode,
  getTranslationKey,
  shouldAddDepthToGridArrIndex,
  toArray,
  translate,
} from '../utils/form-manager.utils';

const allOnlineButtons = (formModel, removeSignOffButton, cancelButtonOnly) => (key) => {
  const isButton = BUTTON_LIST.includes(formModel[key].controlType);

  if (!isButton) {
    return false;
  }

  if (cancelButtonOnly) {
    return formModel[key].buttonType === BUTTON_TYPES.CANCEL;
  }

  const notSaveOffline = formModel[key].buttonType !== BUTTON_TYPES.SAVE_OFFLINE;
  const notSignOff = formModel[key].buttonType !== BUTTON_TYPES.SAVE_AND_SIGNOFF;

  if (removeSignOffButton) {
    return notSaveOffline && notSignOff;
  }

  return notSaveOffline;
};

const allOfflineButtons = (formModel, enableOfflineSaving) => (key) => {
  const buttonTypesToFilter = [
    BUTTON_TYPES.SAVE_AND_SIGNOFF,
    BUTTON_TYPES.SAVE_WITHOUT_SIGNOFF,
    BUTTON_TYPES.OTHER,
  ];

  if (!enableOfflineSaving) {
    buttonTypesToFilter.push(BUTTON_TYPES.SAVE_OFFLINE);
  }

  return (
    BUTTON_LIST.includes(formModel[key].controlType) &&
    !buttonTypesToFilter.includes(formModel[key].buttonType)
  );
};

const onlyRoot = (formModel) => (key) => formModel[key].parent === ROOT;
const filterModelsByListId = (listId) => (key) => listId.includes(key);
const getProp = (model) => (acc, prop) => ({ ...acc, [prop]: model[prop] });
const getProps = (formModel, props) => (acc, key) => {
  const values = props.reduce(getProp(formModel[key]), {});

  return {
    ...acc,
    [key]: values,
  };
};

export const getIndexForControl = createSelector(
  [getFormModel, getRelationShipKeysMap, getComponentProps],
  (formModel, relationShipKeysMap, { id, parentIndex }) => {
    const { controlType, parent } = formModel[id];
    let { entityName = '' } = formModel[id];

    if (controlType === SS_CONTROL_TYPE.ZOOM) {
      entityName = getEntityNameForZoom(formModel, parent);
    }

    if (BOUND_CONTAINERS.includes(controlType)) {
      let parentType = formModel[parent]?.controlType;

      if (
        REPEATING_CONTAINERS.includes(parentType) &&
        controlType === SS_CONTROL_TYPE.PANEL &&
        !shouldAddDepthToGridArrIndex(entityName, parentIndex)
      ) {
        return parentIndex;
      }

      if (FORM_TYPES.includes(parentType)) {
        const parentId = findClosestContainerParent(formModel, id);
        parentType = formModel[parentId]?.controlType;
      }

      const parentEntityName = getParentEntityName(entityName);

      if (
        REPEATING_CONTAINERS.includes(parentType) ||
        relationShipKeysMap[parentEntityName] === '1to1'
      ) {
        if (shouldAddDepthToGridArrIndex(entityName, parentIndex)) {
          return [...parentIndex, 0];
        }

        return getGridArrIndexBasedOnRequiredLength(entityName, parentIndex);
      }
    }

    return getGridArrIndexBasedOnRequiredLength(entityName, parentIndex);
  }
);

export const getIsDownloadInOfflineAvailableForEntity = createSelector(
  [
    getIsOfflineSupportAvailable,
    getWorkflowInfo,
    getEntityData,
    getEntityDefinition,
    getComponentProps,
  ],
  (isOfflineSupportAvailable, { stepId, workflowId }, entityData, entityDefinition) => {
    const phaseData = entityData?.CurrentPhaseStepData;
    const entityType = Object.keys(entityDefinition ?? {})[0];
    const entityId = entityData[entityType]?.ENTITY_ID ?? '';
    const actorCode = localStorage.getItem('actorCode') ?? localStorage.getItem('prevActorCode');
    const isUserOwnerOfTheTask = phaseData?.Owner?.toLowerCase() === actorCode?.toLowerCase();
    const hasReviewOrPerformStepId = includesValueInOptions(phaseData?.StepId, [
      'performaudit',
      'reviewfinding',
    ]);
    const hasReviewOrPerformName = includesValueInOptions(phaseData?.Name, [
      'Perform Audit',
      'Review Finding',
    ]);
    const isAudit = entityId === OFFLINE_ENTITY_TYPE;
    const isReviewFindingOrPerformAudit = hasReviewOrPerformStepId || hasReviewOrPerformName;

    return (
      isOfflineSupportAvailable &&
      !isNilOrEmpty(stepId) &&
      !isNilOrEmpty(workflowId) &&
      isUserOwnerOfTheTask &&
      isReviewFindingOrPerformAudit &&
      isAudit
    );
  }
);

export const getRootElements = createSelector([getFormModel], (formModel) =>
  Object.keys(formModel).filter(onlyRoot(formModel))
);

export const getIsMasterSetupFormViewOnly = createSelector(
  [getFormInfo, getWorkflowInfo, (state) => getManageRights(state)],
  ({ isMasterSetupEntity }, { entityType }, manageRights) =>
    isMasterSetupEntity && !manageRights[entityType]
);

export const getAllButtons = createSelector(
  [
    getFormModel,
    getNetworkStatus,
    getIsOfflineSupportAvailable,
    getIsDownloadInOfflineAvailableForEntity,
    getUnsynchronizedEntityId,
    getIsMasterSetupFormViewOnly,
  ],
  (
    formModel,
    isOnline,
    isOfflineSupportAvailable,
    isDownloadInOfflineAvailableForEntity,
    unsynchronizedEntityId,
    isMasterSetupFormViewOnly
  ) => {
    if (!isOnline && isOfflineSupportAvailable) {
      return Object.keys(formModel).filter(
        allOfflineButtons(formModel, isDownloadInOfflineAvailableForEntity)
      );
    }

    return Object.keys(formModel).filter(
      allOnlineButtons(formModel, unsynchronizedEntityId !== undefined, isMasterSetupFormViewOnly)
    );
  }
);

export const formHasButtons = createSelector([getAllButtons], (buttons) => buttons.length > 0);

export const getFormKey = createSelector([getFormInfo], (formInfo) => formInfo?.formKey);

export const getEntityFromEntityDefinition = createSelector(
  [getEntityDefinition, getComponentProps],
  (entityDefinition, { entityName }) => {
    if (entityName) {
      const split = entityName.split('-');
      const dataPath = split.map((entity, i) => {
        let currentPath = '';
        for (let x = 0; x < i; x++) {
          currentPath += split[x] + '-';
        }
        return (currentPath += entity);
      });
      return _.get(entityDefinition, dataPath);
    }
  }
);

export const getChildrenWithProps = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { childrenNodes, childrenProps }) =>
    Object.keys(formModel)
      .filter(filterModelsByListId(childrenNodes))
      .reduce(getProps(formModel, childrenProps), {})
);

export const getPropByModel = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { id, prop }) => formModel[id] && _.get(formModel, `${id}.${prop}`)
);

export const getIsParentAdvancedTextBox = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { id }) => {
    const parentId = formModel[id] && _.get(formModel, `${id}.parent`);
    return formModel[parentId]?.controlType === SS_CONTROL_TYPE.ADVANCED_TEXT_BOX;
  }
);

export const getModelById = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { id }) => formModel[id]
);

export const getModelByProp = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { prop, value }) =>
    value && Object.keys(formModel).find((key) => formModel[key][prop] === value)
);

export const getOriginalModelById = createSelector(
  [getOriginalFormModel, getComponentProps],
  (originalFormModel, { id }) => originalFormModel[id]
);

export const getClosestParentWithEntityName = createSelector(
  [getFormModel, getEntityType, getComponentProps],
  (formModel, entityType, { id }) => findClosestParentWithEntityName(formModel, entityType, id)
);

/**
 * check if a grid or repeating panel doesn't have direct parent
 * e.g. V_SS_NC -> V_SS_NC-V_SS_NC_PART-V_SS_NC_PART_LOT (V_SS_NC-V_SS_NC_PART is missing)
 */
export const getIsParentlessTable = createSelector(
  [
    getFormModel,
    (state, props) => getClosestParentWithEntityName(state, props),
    getRelationShipKeysMap,
    getComponentProps,
  ],
  (formModel, parentControlEntityName, relationShipKeysMap, { id }) => {
    if (!formModel[id]) {
      return;
    }

    const { entityName, controlType } = formModel[id];

    if (!entityName || !BOUND_CONTAINERS.includes(controlType)) {
      return false;
    }

    const entityNameSplit = entityName.split('-').length;
    const parentControlEntityNameSplit = parentControlEntityName.split('-').length;

    if (entityNameSplit - parentControlEntityNameSplit <= 1) {
      return false;
    }

    const parentEntityName = getParentEntityName(entityName);

    return relationShipKeysMap[parentEntityName] !== '1to1';
  }
);

export const getSsContext = createSelector(
  [
    getFormData,
    getEntityDefinition,
    getTranslations,
    getFormModel,
    getDataSources,
    getAppSettings,
    getRelationShipKeysMap,
    getComponentProps,
    getOrgHierarchy,
    getInstalledVersionsRows,
    getUserRoles,
    getIsParentlessTable,
    getCachedSupportData,
    getSupportData,
  ],
  (
    formData,
    entityDefinition,
    translations,
    formModel,
    dataSources,
    appSettings,
    relationShipKeysMap,
    { index, entityName, formKey },
    orgHierarchy,
    installedVersionsRows,
    userRoles,
    isParentlessTable,
    cachedSupportData,
    supportData
  ) => {
    const entityType = Object.keys(entityDefinition)[0];

    return {
      gridArrIndex: index,
      data: formData,
      entityDefinition,
      entityType,
      contextDataMember: entityName ?? entityType,
      translations,
      formModel,
      dataSources,
      formKey,
      appSettings,
      relationShipKeysMap,
      orgHierarchy,
      installedVersions: installedVersionsRows,
      userRoles,
      isParentlessTable,
      cachedSupportData,
      supportData,
    };
  }
);

export const getEvaluatedConditions = createSelector(
  [getSsContext, getComponentProps],
  (ssContext, { id, prop, defaultValue = false }) => {
    if (!id) {
      return false;
    }

    const result = ssContext.formModel[id][prop];

    if (_.isNil(result)) {
      return defaultValue;
    }

    if (
      typeof result === 'string' &&
      (result.toLowerCase() === 'true' || result.toLowerCase() === 'false')
    ) {
      return result.toLowerCase() === 'true';
    }

    return Array.isArray(result) || result?.Conditions?.length
      ? evaluateConditions({
          conditions: Array.isArray(result) ? result : result.Conditions,
          ssContext,
        })
      : result;
  }
);

export const getEvaluatedExpression = createSelector(
  [getSsContext, getComponentProps],
  (ssContext, { id, prop, defaultValue }) => {
    if (!id) {
      return false;
    }

    const propValue = ssContext.formModel[id]?.[prop];

    if (!propValue && !_.isBoolean(propValue)) {
      return defaultValue;
    }

    const expression =
      propValue !== undefined && !_.isBoolean(propValue) && propValue.split('eval||')[1];

    if (!expression) {
      return propValue;
    }

    const evaluatedExpression = evalSSEexpression(expression, ssContext);

    return evaluatedExpression === '_eval_error_'
      ? defaultValue || expression
      : evaluatedExpression;
  }
);

export const getPropTranslationSelector = createSelector(
  [getEntityDefinition, getTranslations, getComponentProps],
  (entityDefinition, translations, { entityId, entityName }) => {
    // recursive search through entity definition for node which is equal to entityId
    let translation;
    const parentNode = getParentNode(entityDefinition, entityName);

    const searchCaption = (entityParentNode, entityId) => {
      !translation &&
        parentNode &&
        Object.entries(entityParentNode).forEach(([key, node]) => {
          if (!['properties'].includes(key)) {
            if (key === entityId) {
              const caption = (node && node.properties && node.properties.Caption) || '';
              const { Entity } = translations;
              translation = Entity && Entity[caption];
            } else if (_.isNil(translation)) {
              searchCaption(node, entityId);
            }
          }
        });
      return translation;
    };
    return searchCaption(parentNode, entityId);
  }
);

export const getEntityTranslation = createSelector(
  [getTranslations, getFormModel, getComponentProps],
  (translations, formModel, { id }) => {
    const entityName = formModel[id].entityName.split('-').slice(-1)[0];
    return translate(translations, entityName);
  }
);

export const getDataSource = createSelector(
  [getDataSources, getEntityData, getComponentProps],
  (dataSources, entityData = {}, { dataSourceKey }) => {
    // check if dataSourceKey exists in the dataSources store
    if (
      typeof dataSourceKey === 'string' &&
      dataSources[dataSourceKey] &&
      !PREDEFINED_DATA_SOURCES.includes(dataSourceKey)
    ) {
      return {
        ...dataSources[dataSourceKey],
        dataSourceKey,
      };
    }
    // try to get datasource values from entityDataStore
    const entityKey = dataSourceKey?.substring(0, dataSourceKey?.indexOf('List')); // cut off 'List'
    return toArray(entityData[entityKey]);
  }
);

export const getHeaderInfo = createSelector(
  [getRootElements, getFormModel, getComponentProps],
  (rootElements, formModel, { formKey, pathname }) => {
    const root = formModel[rootElements[0]];
    const panelHeader = formModel[root?.childrenNodes[0]];
    const isPanel = panelHeader?.controlType === SS_CONTROL_TYPE.PANEL;
    const showDetails = [FORM_KEYS.DETAIL, FORM_KEYS.LINK_PREVIEW].includes(formKey);
    const isEntityDisplayRoute = pathname === ENTITY_DISPLAY;

    return isPanel && showDetails && isEntityDisplayRoute ? panelHeader : {};
  }
);

export const isHeaderComponent = createSelector(
  [getHeaderInfo, getComponentProps],
  (headerInfo, { id }) => headerInfo.controlId === id
);

export const isInHeaderComponent = createSelector(
  [getHeaderInfo, getFormModel, getComponentProps],
  (headerInfo, formModel, { id }) => formModel[id]?.parent === headerInfo.controlId
);

export const isDataTypeDate = createSelector(
  [getEntityDefinition, getEntityType, getComponentProps],
  (entityDefinition, entityType, { dataMember, dataField }) => {
    const dateType = getDataType(entityDefinition, entityType, dataMember, dataField);
    return dateType === 'System.DateTime';
  }
);

export const getTextViewValue = createSelector(
  [isDataTypeDate, getEvaluatedExpression, getComponentProps],
  (isDataTypeDateInt, translateData, { value, translateString, controlType, formatType }) => {
    return controlType !== SS_CONTROL_TYPE.TEXT_VIEW || value === '---'
      ? value
      : _.isNil(value)
      ? ''
      : isDataTypeDateInt
      ? formatStringToDateType(value, formatType === 'dateTime')
      : isTrue(translateData)
      ? translateString(value)
      : value;
  }
);

export const getEvaluatedDefaultTabIndex = createSelector(
  [getSsContext, getChildrenWithProps, getComponentProps],
  (ssContext, childrenSingleTab, { childrenNodes }) => {
    const defaultTabId = childrenNodes.find((childId) => {
      const propValue = childrenSingleTab[childId]?.defaultTab;

      if (_.isNil(propValue)) {
        return false;
      }

      if (
        (typeof propValue === 'string' && !propValue.includes('eval||')) ||
        typeof propValue === 'boolean'
      ) {
        return isTrue(propValue);
      }

      const expression = propValue.split('eval||')[1];
      return evalSSEexpression(expression, ssContext);
    });

    return defaultTabId ? childrenNodes.indexOf(defaultTabId) : 0;
  }
);

export const getTranslatedLabel = createSelector(
  [getTranslations, getEntityDefinition, getComponentProps],
  (translations, entityDefinition, { label, entityName, fieldName }) => {
    const entityType = Object.keys(entityDefinition)[0];
    const translationKey =
      label ?? getTranslationKey(entityDefinition, entityType, entityName, fieldName);

    return translate(translations, translationKey);
  }
);

export const getReadOnlyControlContent = createSelector(
  [getTranslations, getComponentProps],
  (translations, { value, defaultValue, controlType }) => {
    if (controlType === SS_CONTROL_TYPE.CHECK_BOX) {
      const isSmartSolveBooleanChecked = isTrue(value) || value === '1' || value === 1;

      return isSmartSolveBooleanChecked
        ? translate(translations, 'Yes')
        : translate(translations, 'No');
    }

    return isNilOrEmpty(value) ? defaultValue : value;
  }
);

export const getHelperError = createSelector(
  [getErrors, getEntityData, getEntityDefinition, getComponentProps],
  (errors, entityData, entityDefinition, { entityName, fieldName, index }) => {
    if (isNilOrEmpty(errors)) {
      return;
    }

    const primaryKey = getEntityPrimaryKey(entityDefinition, entityName);
    const dataPath = getDataPath(entityName, index, entityDefinition);
    const entityDataItem = _.get(entityData, dataPath);
    const helperError = errors.find(
      (error) => error.rowID === entityDataItem?.[primaryKey] && error.columnName === fieldName
    );
    return helperError;
  }
);

export const getSupportDataForDataSource = createSelector(
  [getSsContext, getComponentProps],
  (
    ssContext,
    { dataSourceName, dataSourceFilter, dataSourceFilterFromDataSource, isGridInitialization }
  ) => {
    const supportData = ssContext.supportData;
    const supportDataItem = _.get(supportData, dataSourceName);

    if (isNilOrEmpty(supportDataItem)) {
      return {};
    }

    const filter = [
      ...(dataSourceFilter?.Conditions ?? []),
      ...(dataSourceFilterFromDataSource?.Conditions ?? []),
    ];

    return !isGridInitialization
      ? applyFilterToDataSource(ssContext, supportDataItem, filter)
      : supportData;
  }
);

export const getEvaluatedConditionsForInteractionAction = createSelector(
  [getEvaluatedConditions, getSsContext, getComponentProps],
  (evaluatedConditionsResult, ssContext, { id, prop, defaultValue = false }) => {
    if (!id) {
      return false;
    }

    const result = ssContext.formModel[id][prop];

    if (_.isNil(result) || typeof evaluatedConditionsResult !== 'boolean') {
      return defaultValue;
    }

    return result?.conditionMet === false ? !evaluatedConditionsResult : evaluatedConditionsResult;
  }
);

export const getIsControlAdded = createSelector(
  [getLayoutChanges, getComponentProps],
  ({ AddedFields = [] }, { id }) => AddedFields.some(({ controlId }) => controlId === id)
);

export const getIsControlChanged = createSelector(
  [getDeltaChanges, getComponentProps],
  ({ controls = [] }, { id }) =>
    controls.some(({ ControlId, Properties = [] }) => ControlId === id && Properties.length)
);

export const getFormHasChanges = createSelector(
  [getLayoutChanges, getDeltaChanges],
  ({ AddedFields = [], DeletedFields = [], MovedFields = [] }, { controls = [] }) =>
    !!(
      AddedFields.length ||
      DeletedFields.length ||
      MovedFields.length ||
      controls.some(({ Properties = [] }) => Properties.length)
    )
);

export const getDeltaChangesForControl = createSelector(
  [getDeltaChanges, getComponentProps],
  (deltaChanges, { id }) =>
    deltaChanges.controls?.find(({ ControlId }) => ControlId === id)?.Properties ?? []
);
